import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const CartCtx = createContext(null);
// Cart contents are not sensitive — persisting via localStorage keeps the cart
// across refreshes without any auth implication.
const KEY = "ayur_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch (e) {
      console.error("cart parse failed:", e);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      console.error("cart persist failed:", e);
    }
  }, [items]);

  // Carts saved before product images were served from /api/products/{id}/image
  // hold a blank thumbnail. Backfill those once from the catalogue.
  useEffect(() => {
    if (!items.some((x) => !x.image)) return;
    let cancelled = false;
    api
      .get("/products")
      .then(({ data }) => {
        if (cancelled) return;
        const byId = new Map(data.map((p) => [p.id, p.image]));
        setItems((prev) => prev.map((x) => (x.image ? x : { ...x, image: byId.get(x.id) || "" })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = useCallback((product, qty = 1) => {
    setItems((p) => {
      const ex = p.find((x) => x.id === product.id);
      if (ex) return p.map((x) => (x.id === product.id ? { ...x, quantity: x.quantity + qty } : x));
      return [...p, { id: product.id, name: product.name, price: product.price, image: product.image, quantity: qty }];
    });
  }, []);

  const remove = useCallback((id) => setItems((p) => p.filter((x) => x.id !== id)), []);
  const setQty = useCallback(
    (id, q) => setItems((p) => p.map((x) => (x.id === id ? { ...x, quantity: Math.max(1, q) } : x))),
    []
  );
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const total = items.reduce((s, x) => s + x.price * x.quantity, 0);
    const count = items.reduce((s, x) => s + x.quantity, 0);
    return { items, add, remove, setQty, clear, total, count };
  }, [items, add, remove, setQty, clear]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
